import { useGetUserCalenderInfo } from '@forwardflow/hooks/useGetUserCalenderInfo'
import { useGetAllLeadSources } from '@forwardflow/features/campaign'
import { UserService } from '@forwardflow/services/UserService'
import { useNotificationBarState } from '@forwardflow/store/notificationBar'
import { NotificationBar } from '@forwardflow/components/Dashboard/NotificationBar'
import { Button, Select } from 'antd'
import { useEffect, useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { DashboardTabs } from './DashboardTabs'
import { OverviewContent } from './OverviewContent'
import { LeadSourceContent } from './LeadSourceContent'
import { CommunicationContent } from './CommunicationContent'
import { TeamPerformanceContent } from './TeamPerformanceContent'
import useAuthUser from '@forwardflow/hooks/customHooks/useAuthUser'
import { useAppStore } from '@forwardflow/store'

export const DashboardContent = () => {
   const [searchParams, setSearchParams] = useSearchParams()
   const timePeriod = searchParams.get('timePeriod') || 'allTime'
   const tabParam = searchParams.get('tab') || 'overview'
   const { currentOrganization } = useAppStore()
   const agentType = searchParams.get('agentType') || 'all'
   const agentOptions = [
      { value: 'all', label: 'Both Agents & FF' },
      { value: 'agent', label: 'Agents Only' },
      { value: 'ff', label: 'FF Only' },
   ]
   const { data: user } = useAuthUser({ revalidateOnMount: true })

   const adminEmails =
      user?.email === 'operator@forward-flow.com' ||
      user?.email === 'brannon@forward-flow.com' ||
      user?.email === 'engineered@forward-flow.com'

   const monthOptions = [
      { value: 'allTime', label: 'Last 6 Months' },
      // { value: 'lastMonth', label: 'Last Month' },
      // { value: 'last7Days', label: 'Last 7 Days' },
   ]

   const { data: UserCalenderInfo, isLoading: UserCalenderInfoLoading } =
      useGetUserCalenderInfo(!!currentOrganization)
   const { data: activeCampaigns, isLoading: activeCampaignsLoading } =
      useGetAllLeadSources({ page: 1, limit: 1, status: 'active' })
   const hasNoActiveCampaigns =
      !activeCampaignsLoading && (!activeCampaigns || activeCampaigns.length === 0)
   const { isVisible, setIsVisible } = useNotificationBarState()
   const [activeTab, setActiveTab] = useState(tabParam)

   useEffect(() => {
      if (!UserCalenderInfo?.email || !UserCalenderInfo?.firstName) {
         setIsVisible(true)
      } else {
         setIsVisible(false)
      }
   }, [UserCalenderInfo])

   const handleCalendarConnect = async () => {
      try {
         const redirectUrl = encodeURIComponent(
            `${window.location.origin}/dashboard`
         )
         const response = await UserService.getConnectUserWithCalender(
            redirectUrl,
            currentOrganization
         )

         if (response.redirectUrl) {
            window.location.href = response.redirectUrl
         }
      } catch (e) {
         console.error('Error connecting calendar:', e)
      }
   }

   const renderTabContent = () => {
      switch (activeTab) {
         case 'overview':
            return <OverviewContent />
         case 'lead-source':
            return <LeadSourceContent />
         case 'communication':
            return <CommunicationContent />
         case 'team-performance':
            return <TeamPerformanceContent />
         default:
            return <OverviewContent />
      }
   }
   const handleTimePeriodChange = (value: string) => {
      const params = new URLSearchParams(searchParams)
      params.set('timePeriod', value)
      setSearchParams(params)
   }

   const handleTabChange = (tab: string) => {
      const params = new URLSearchParams(searchParams)
      params.set('tab', tab)
      setSearchParams(params)
   }

   const handleAgentTypeChange = (value: string) => {
      const params = new URLSearchParams(searchParams)
      params.set('agentType', value)
      setSearchParams(params)
   }

   useEffect(() => {
      setActiveTab(tabParam)
   }, [tabParam])

   return (
      <>
         <div>
            {!UserCalenderInfoLoading && isVisible && !adminEmails && (
               <NotificationBar
                  message="Connect your Google Calendar to enable automated appointment scheduling, availability syncing, and follow-up tracking."
                  action={
                     <Button
                        onClick={handleCalendarConnect}
                        className="rounded-lg bg-[rgba(217,217,217,0.2)] hover:!bg-[rgba(217,217,217,0.2)] hover:scale-105
                        hover:!text-white border-none px-6 !text-white transition-all duration-200"
                     >
                        Connect your calendar
                     </Button>
                  }
               />
            )}
            {hasNoActiveCampaigns && (
               <NotificationBar
                  message="You don't have any active campaigns. Create or activate a campaign to start engaging with leads."
                  action={
                     <Link to="/campaign">
                        <Button
                           className="rounded-lg bg-[rgba(217,217,217,0.2)] hover:!bg-[rgba(217,217,217,0.2)] hover:scale-105
                           hover:!text-white border-none px-6 !text-white transition-all duration-200"
                        >
                           Go to Campaigns
                        </Button>
                     </Link>
                  }
               />
            )}
            <div className="flex flex-col justify-between gap-4 xl:flex-row">
               <DashboardTabs
                  activeTab={activeTab}
                  onTabChange={handleTabChange}
               />
               <div className="flex gap-2 border4 dashboard-dropdown">
                  {activeTab === 'communication' && (
                     <Select
                        defaultValue={agentType}
                        options={agentOptions}
                        className="h-10 text-white min-w-36"
                        dropdownStyle={{
                           backgroundColor: '#18181B',
                           color: 'white',
                        }}
                        onChange={handleAgentTypeChange}
                        value={agentType}
                     />
                  )}
                  <Select
                     defaultValue="allTime"
                     options={monthOptions}
                     className="h-10 text-white hide-arrow"
                     onChange={handleTimePeriodChange}
                     value={timePeriod}
                     disabled
                     dropdownStyle={{
                        backgroundColor: '#18181B',
                        color: 'white',
                     }}
                  />
               </div>
            </div>

            {renderTabContent()}
         </div>
      </>
   )
}
